import { Theme } from "@mui/system";
import { push } from "connected-react-router";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Box } from "@tsp/tsp-frontend-components/mui";
import { Header } from "@tsp/tsp-frontend-components/tsp";
import { sxMerge } from "@tsp/utils/styles/sxMerge";

import { Notifications } from "@tsp-core/notifier/components/notifications";

import { selectProjectName } from "../../../config";
import { logoutAction } from "../../../user";
import { useMainMenu } from "../../hooks/useMainMenu";
import { selectUserData } from "../../selectors";

import { PageLoader } from "../PageLoader";

interface Props {
  loading?: boolean;
  hideHeader?: boolean;
}

const classes = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100%",
  } as const,
  content: (theme: Theme) =>
    ({
      opacity: 0,
      display: "flex",
      flex: "1 1 auto",
      transition: "0.2s",
      alignItems: "center",
      flexDirection: "column",
      padding: `${theme.spacing(4)} 0`,
    }) as const,
  fade: {
    opacity: 1,
  },
  loaderPosition: {
    width: "100%",
    position: "relative",
  } as const,
  loader: {
    top: 0,
    left: 0,
    width: "100%",
    position: "absolute",
  } as const,
};

export function Layout({ children, loading, hideHeader }: PropsWithChildren<Props>) {
  const dispatch = useDispatch();
  const [viewChildren, setViewChildren] = useState(false);

  const menuProps = useMainMenu();

  const userData = useSelector(selectUserData);
  const projectName = useSelector(selectProjectName);

  const contentStyles = sxMerge(classes.content, viewChildren ? classes.fade : undefined);

  const showContent = useCallback(() => setViewChildren(true), []);
  const logOutHandler = useCallback(() => dispatch(logoutAction()), [dispatch]);
  const onSettingsLinkClickHandler = useCallback(() => dispatch(push("/settings")), [dispatch]);

  const userProps = useMemo(() => {
    return userData
      ? {
          ...userData,
          onLogoutClick: logOutHandler,
          showSettingsLink: true,
          onSettingsLinkClick: onSettingsLinkClickHandler,
          settingsLinkText: "Employee settings",
        }
      : undefined;
  }, [logOutHandler, onSettingsLinkClickHandler, userData]);

  return (
    <Box sx={classes.wrapper}>
      {!hideHeader && <Header menuProps={menuProps} userProps={userProps} projectName={projectName} />}
      <Box sx={classes.loaderPosition}>
        <Box sx={classes.loader}>
          <PageLoader show={loading} onHide={showContent} />
        </Box>
      </Box>
      {loading ? null : <Box sx={contentStyles}>{children}</Box>}
      <Notifications />
    </Box>
  );
}
